"use client";

import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  memo,
  useDeferredValue,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { useInView } from "react-intersection-observer";
import { ContributionsControls } from "./_controls.client";
import {
  parseContributionFilters,
  serializeContributionFilters,
} from "./_filters";
import {
  ContributionGroupDetails,
  ContributionTechnologyBadges,
} from "@/components/contribution-card";
import { formatStars, formatYears } from "@/util/formatters";
import { ContributionTypeBreakdown } from "@/components/contribution-type-breakdown";
import { TechnologyBreakdown } from "@/components/technology-breakdown";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ContributionType } from "@/data/contribution-types";
import {
  groupContributions,
  sortContributionGroups,
  filterContributionGroups,
  SortOption,
  getDefaultSortDirection,
  countCommitsInGroup,
  countContributionsInGroup,
  getUniqueYearsInGroup,
  getSafeRepositoryId,
  contributionGroupMatchesOnlyHiddenText,
} from "@/util/contributions";
import type {
  ContributionTypeStats,
  ContributionSummary,
  TechnologyStats,
  SortDirection,
  ContributionGroup,
  ContributionRow,
} from "@/util/contributions";
import { GitCommit, FolderGit2, Star, Calendar } from "lucide-react";
import {
  chevronDownIcon,
  chevronRightIcon,
  externalLinkIcon,
  gitCommitHorizontalIcon,
  starIcon,
} from "@/components/ui/static-lucide";
import { ExternalLink } from "@/components/ui/external-link";
import { MetricCard } from "@/components/metric-card";
import { NotableProjectsHighlight } from "@/components/notable-projects-highlight";
import { MOUNTS_NEAR_VIEWPORT } from "@/constants/mounts-near-viewport";
import dynamic from "next/dynamic";

// The chart pulls in the charting library, which is heavy and has no SEO
// value; loading it lazily on the client keeps it out of the navigation
// critical path. The placeholder reserves the rendered height to avoid
// layout shift.
const CommitsTimelineChart = dynamic(
  () =>
    import("@/components/commits-timeline-chart").then(
      (module) => module.CommitsTimelineChart,
    ),
  {
    ssr: false,
    loading: () => <CommitsTimelineChartPlaceholder />,
  },
);

// Mirrors the section structure of CommitsTimelineChart (same heading text
// and chart height) so nothing shifts when the real chart mounts.
function CommitsTimelineChartPlaceholder() {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
        Commits Over Time
      </h2>
      <div
        aria-hidden="true"
        className="bg-muted/40 h-40 w-full rounded-xl motion-safe:animate-pulse sm:h-48"
      />
    </section>
  );
}

// The chart sits below the fold on every viewport. Fetching and evaluating
// the charting library the moment the page hydrates spent main-thread time
// on a section nobody had reached yet, so the import starts when the
// placeholder comes within a viewport-height of being scrolled into view.
const CHART_MOUNT_ROOT_MARGIN = "100% 0px";

function CommitsTimelineChartNearViewport({
  commitsByYear,
}: {
  commitsByYear: [number, number][];
}) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: CHART_MOUNT_ROOT_MARGIN,
  });
  return (
    <div
      ref={ref}
      {...{
        [MOUNTS_NEAR_VIEWPORT.attribute]: inView
          ? MOUNTS_NEAR_VIEWPORT.mounted
          : MOUNTS_NEAR_VIEWPORT.pending,
      }}
    >
      {inView ? (
        <CommitsTimelineChart commitsByYear={commitsByYear} />
      ) : (
        <CommitsTimelineChartPlaceholder />
      )}
    </div>
  );
}

type ContributionsClientProps = {
  stats: ContributionTypeStats[];
  technologyStats: TechnologyStats[];
  summary: ContributionSummary;
  initialRows: ContributionRow[];
  commitsByYear: [number, number][];
  uniqueTechnologies: string[];
};

function RepositoryLinkLabel({ name }: { name: string }) {
  const finalBreak = Math.max(name.lastIndexOf("/"), name.lastIndexOf("-"));
  const tailStart =
    finalBreak === -1 ? Math.max(name.length - 1, 0) : finalBreak + 1;
  const prefix = name.slice(0, tailStart);
  const tail = name.slice(tailStart);

  return (
    <>
      {prefix}
      <span className="whitespace-nowrap" data-testid="repository-name-tail">
        {tail}
        {externalLinkIcon(
          "text-muted-foreground ml-1 inline size-4 align-[-0.125em] lg:size-3.5",
        )}
      </span>
    </>
  );
}

export function ContributionsClient({
  stats,
  technologyStats,
  summary,
  initialRows,
  commitsByYear,
  uniqueTechnologies,
}: ContributionsClientProps) {
  const router = useRouter();
  const pathname = usePathname();

  const [query, setQuery] = useState("");
  // Defer the query value to keep the UI responsive while filtering the large list of contributions.
  const deferredQuery = useDeferredValue(query);

  const [sort, setSort] = useState<SortOption>(SortOption.Stars);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [typeFilter, setTypeFilter] = useState<ContributionType | null>(null);
  const [techFilter, setTechFilter] = useState<string | null>(null);

  // The route is statically prerendered with the default (unfiltered) view so
  // the full table stays in the HTML; filter deeplinks (?q, ?sort, ?dir,
  // ?type, ?tech) are applied after hydration. Reading them via
  // useSearchParams would force this subtree out of the static HTML.
  useEffect(() => {
    const filters = parseContributionFilters(
      new URLSearchParams(window.location.search),
      uniqueTechnologies,
    );
    // Setting state in an effect is deliberate here: the deeplink can only be
    // read on the client, after the statically prerendered default view has
    // hydrated, so this is the earliest point the filters can be adopted.
    /* eslint-disable react-hooks/set-state-in-effect -- see above */
    setQuery(filters.query);
    setSort(filters.sort);
    setSortDirection(filters.sortDirection);
    setTypeFilter(filters.typeFilter);
    setTechFilter(filters.techFilter);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [uniqueTechnologies]);

  // Build contribution data from props instead of importing REPOSITORIES
  const rows = initialRows;
  const groups = useMemo(() => groupContributions(rows), [rows]);

  // Filter and sort
  const filteredGroups = useMemo(
    () =>
      filterContributionGroups(groups, deferredQuery, typeFilter, techFilter),
    [groups, deferredQuery, typeFilter, techFilter],
  );
  const sortedGroups = useMemo(
    () => sortContributionGroups(filteredGroups, sort, sortDirection),
    [filteredGroups, sort, sortDirection],
  );

  // Update URL when filters change
  const updateUrl = useCallback(
    (
      newQuery: string,
      newSort: SortOption,
      newSortDirection: SortDirection,
      newTypeFilter: ContributionType | null,
      newTechFilter: string | null,
    ) => {
      const params = serializeContributionFilters(
        {
          query: newQuery,
          sort: newSort,
          sortDirection: newSortDirection,
          typeFilter: newTypeFilter,
          techFilter: newTechFilter,
        },
        new URLSearchParams(window.location.search),
      );

      const queryString = params.toString();
      const currentQueryString = new URLSearchParams(
        window.location.search,
      ).toString();

      // Only update if parameters actually changed
      if (queryString !== currentQueryString) {
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.replace(newUrl, { scroll: false });
      }
    },
    [pathname, router],
  );

  // Debounce URL updates for search
  useEffect(() => {
    const timeout = setTimeout(() => {
      updateUrl(query, sort, sortDirection, typeFilter, techFilter);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, sort, sortDirection, typeFilter, techFilter, updateUrl]);

  const handleSearchChange = useCallback((value: string) => {
    setQuery(value);
  }, []);

  const handleSortChange = useCallback((value: SortOption) => {
    setSort(value);
    // Reset to default direction when changing sort type
    setSortDirection(getDefaultSortDirection(value));
  }, []);

  const handleSortDirectionToggle = useCallback(() => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  }, []);

  const handleTypeFilterChange = useCallback(
    (value: ContributionType | null) => {
      setTypeFilter(value);
    },
    [],
  );

  const handleTechFilterChange = useCallback((value: string | null) => {
    setTechFilter(value);
  }, []);

  // Count visible contributions
  // Optimized with useMemo to prevent O(N*M) calculation on every render.
  // Correctness covered by unit tests (src/util/contributions.test.ts) and E2E flows.
  const visibleContributions = useMemo(
    () =>
      sortedGroups.reduce((acc, group) => acc + countCommitsInGroup(group), 0),
    [sortedGroups],
  );

  const isFiltered = Boolean(deferredQuery || typeFilter || techFilter);

  // Get contribution types that have at least one contribution
  const availableTypes = useMemo(
    () => stats.filter((stat) => stat.count > 0).map((stat) => stat.type),
    [stats],
  );

  return (
    <div className="space-y-8">
      {/* Notable projects highlight */}
      <NotableProjectsHighlight />

      {/* Stats overview - single column on tiny screens, 2x2 on mobile, 4 on desktop */}
      <div
        data-testid="contribution-stats-overview"
        className="xs:grid-cols-2 xs:[&>*:nth-child(-n+2)]:border-b xs:[&>*:nth-child(n+3)]:border-b-0 xs:[&>*:nth-child(odd)]:border-r grid grid-cols-1 gap-0 overflow-hidden rounded-xl border border-[rgba(0,0,0,0.05)] bg-[rgba(0,0,0,0.02)] *:border-[rgba(0,0,0,0.05)] sm:grid-cols-2 lg:grid-cols-4 lg:*:border-r-0 dark:border-[rgba(255,255,255,0.05)] dark:bg-[#0f1011] dark:*:border-[rgba(255,255,255,0.05)] [&>*:not(:last-child)]:border-b lg:[&>*:nth-child(-n+3)]:border-r lg:[&>*:nth-child(-n+4)]:border-b-0"
      >
        <MetricCard
          icon={FolderGit2}
          title="Repositories"
          value={summary.totalRepositories.toLocaleString()}
          cardClassName="pb-4"
          contentClassName="text-3xl md:text-4xl font-semibold"
          containerTestId="contribution-stats-card-repositories"
        />
        <MetricCard
          icon={GitCommit}
          title="Commits Merged"
          value={summary.totalCommits.toLocaleString()}
          cardClassName="pb-4"
          contentClassName="text-3xl md:text-4xl font-semibold"
          containerTestId="contribution-stats-card-commits-merged"
        />
        <MetricCard
          icon={Star}
          title="Combined Stars"
          value={formatStars(summary.totalStars)}
          cardClassName="pb-4"
          contentClassName="text-3xl md:text-4xl font-semibold"
          containerTestId="contribution-stats-card-combined-stars"
        />
        <MetricCard
          icon={Calendar}
          title="Years Active"
          value={`${summary.yearRange.min}-${summary.yearRange.max}`}
          cardClassName="pb-4"
          contentClassName="text-3xl md:text-4xl font-semibold"
          containerTestId="contribution-stats-card-years-active"
        />
      </div>

      {/* Commits Timeline Chart */}
      <CommitsTimelineChartNearViewport commitsByYear={commitsByYear} />

      {/* Breakdowns - side by side on desktop, clickable to filter */}
      <div className="grid gap-x-10 gap-y-8 lg:grid-cols-2">
        <ContributionTypeBreakdown
          stats={stats}
          activeType={typeFilter}
          onTypeClick={handleTypeFilterChange}
        />
        <TechnologyBreakdown
          stats={technologyStats}
          activeTech={techFilter}
          onTechClick={handleTechFilterChange}
        />
      </div>

      {/* Controls with integrated results count */}
      <ContributionsControls
        query={query}
        sort={sort}
        sortDirection={sortDirection}
        typeFilter={typeFilter}
        techFilter={techFilter}
        technologies={uniqueTechnologies}
        availableTypes={availableTypes}
        onSearchChange={handleSearchChange}
        onSortChange={handleSortChange}
        onSortDirectionToggle={handleSortDirectionToggle}
        onTypeFilterChange={handleTypeFilterChange}
        onTechFilterChange={handleTechFilterChange}
        resultCount={{
          filteredProjects: sortedGroups.length,
          totalProjects: groups.length,
          visibleCommits: visibleContributions,
          isFiltered,
        }}
      />

      {/* Contributions list */}
      {sortedGroups.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-muted-foreground text-base">
            No contributions found matching your filters
          </p>
        </div>
      ) : (
        <ContributionsList groups={sortedGroups} query={deferredQuery} />
      )}
    </div>
  );
}

/**
 * The responsive contributions list and its expansion state.
 *
 * The state lives here rather than in `ContributionsClient` so that expanding a
 * group re-renders the list alone. Its siblings up there are the recharts
 * timeline, four metric cards, two breakdowns and the controls bar, none of them
 * memoized, and a disclosure toggle has no business re-rendering any of them.
 */
const ContributionsList = memo(function ContributionsList({
  groups,
  query,
}: {
  groups: ContributionGroup[];
  query: string;
}) {
  const [manuallyExpandedRows, setManuallyExpandedRows] = useState<Set<string>>(
    new Set(),
  );
  const [autoCollapsedRows, setAutoCollapsedRows] = useState<{
    query: string;
    rows: Set<string>;
  }>({ query: "", rows: new Set() });

  // Expose otherwise unexplained search results without expanding visible-field
  // matches or the unfiltered list. See
  // adr/0010-expand-contributions-for-hidden-search-matches.md.
  const autoExpandedRows = useMemo(
    () =>
      new Set(
        groups
          .filter((group) =>
            contributionGroupMatchesOnlyHiddenText(group, query),
          )
          .map((group) => group.primaryRow.repositoryId),
      ),
    [groups, query],
  );
  const collapsedRowsForQuery = useMemo(
    () =>
      autoCollapsedRows.query === query
        ? autoCollapsedRows.rows
        : new Set<string>(),
    [autoCollapsedRows, query],
  );

  const toggleRow = useCallback(
    (repositoryId: string, isExpanded: boolean) => {
      const isAutoExpanded = autoExpandedRows.has(repositoryId);

      setManuallyExpandedRows((previous) => {
        const next = new Set(previous);
        if (isExpanded) {
          next.delete(repositoryId);
        } else if (!isAutoExpanded) {
          next.add(repositoryId);
        }
        return next;
      });

      if (isAutoExpanded) {
        setAutoCollapsedRows((previous) => {
          const next =
            previous.query === query
              ? new Set(previous.rows)
              : new Set<string>();
          if (isExpanded) {
            next.add(repositoryId);
          } else {
            next.delete(repositoryId);
          }
          return { query, rows: next };
        });
      }
    },
    [autoExpandedRows, query],
  );

  const isGroupExpanded = useCallback(
    (repositoryId: string) => {
      if (manuallyExpandedRows.has(repositoryId)) return true;
      if (!autoExpandedRows.has(repositoryId)) return false;
      return !collapsedRowsForQuery.has(repositoryId);
    },
    [autoExpandedRows, collapsedRowsForQuery, manuallyExpandedRows],
  );

  return (
    <ContributionsTable
      groups={groups}
      query={query}
      isGroupExpanded={isGroupExpanded}
      onToggle={toggleRow}
    />
  );
});

/**
 * One semantic table that presents each row group as a card below `lg`.
 * Keep one responsive tree to avoid restoring the duplicate DOM cost in
 * adr/0009-render-contributions-once-as-a-responsive-table.md.
 */
const ContributionsTable = memo(function ContributionsTable({
  groups,
  query,
  isGroupExpanded,
  onToggle,
}: {
  groups: ContributionGroup[];
  query: string;
  isGroupExpanded: (repositoryId: string) => boolean;
  onToggle: (repositoryId: string, isExpanded: boolean) => void;
}) {
  return (
    <div
      className="overflow-hidden lg:rounded-xl lg:border"
      style={{ borderColor: "var(--table-border-color)" }}
      data-testid="contributions-list"
    >
      <div className="contain-[paint] lg:overflow-x-auto">
        <Table
          className="block border-t-0 lg:table"
          data-testid="contributions-table"
          role="table"
        >
          <TableHeader
            className="sr-only lg:not-sr-only lg:table-header-group"
            role="rowgroup"
          >
            <TableRow role="row">
              <TableHead className="w-8" role="columnheader">
                <span className="sr-only">Expand details</span>
              </TableHead>
              <TableHead className="whitespace-nowrap" role="columnheader">
                Repository
              </TableHead>
              <TableHead
                className="text-right whitespace-nowrap"
                role="columnheader"
              >
                Stars
              </TableHead>
              <TableHead role="columnheader">Technologies</TableHead>
              <TableHead className="whitespace-nowrap" role="columnheader">
                Years
              </TableHead>
              <TableHead
                className="text-right whitespace-nowrap"
                role="columnheader"
              >
                Commits
              </TableHead>
            </TableRow>
          </TableHeader>
          {groups.map((group) => {
            const isExpanded = isGroupExpanded(group.primaryRow.repositoryId);
            return (
              <ContributionsTableRow
                key={group.primaryRow.repositoryId}
                group={group}
                query={query}
                isExpanded={isExpanded}
                onToggle={onToggle}
              />
            );
          })}
        </Table>
      </div>
    </div>
  );
});

const ContributionsTableRow = memo(function ContributionsTableRow({
  group,
  query,
  isExpanded,
  onToggle,
}: {
  group: ContributionGroup;
  query: string;
  isExpanded: boolean;
  onToggle: (id: string, isExpanded: boolean) => void;
}) {
  const chevronIcon = isExpanded ? chevronDownIcon : chevronRightIcon;
  const detailsId = getSafeRepositoryId(group.primaryRow.repositoryId);

  const commitCount = useMemo(() => countCommitsInGroup(group), [group]);
  const contributionCount = useMemo(
    () => countContributionsInGroup(group),
    [group],
  );

  const years = useMemo(() => getUniqueYearsInGroup(group), [group]);

  const handleRowClick = useCallback(() => {
    onToggle(group.primaryRow.repositoryId, isExpanded);
  }, [onToggle, group.primaryRow.repositoryId, isExpanded]);

  const handleButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggle(group.primaryRow.repositoryId, isExpanded);
    },
    [onToggle, group.primaryRow.repositoryId, isExpanded],
  );

  return (
    <TableBody
      className="mb-6 block rounded-lg border border-[rgba(0,0,0,0.05)] bg-[rgba(0,0,0,0.02)] last:mb-0 lg:table-row-group lg:border-0 lg:bg-transparent dark:border-[rgba(255,255,255,0.05)] dark:bg-[#0f1011] dark:lg:bg-transparent lg:[&_tr:last-child]:border-b!"
      data-testid="contribution-card"
      role="rowgroup"
    >
      <TableRow
        className="lg:hover:bg-muted/50 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-3 border-0 p-4 hover:bg-transparent sm:p-5 lg:table-row lg:cursor-pointer lg:border-b lg:p-0"
        onClick={(event) => {
          if (
            window.getComputedStyle(event.currentTarget).display === "table-row"
          ) {
            handleRowClick();
          } else {
            event.stopPropagation();
          }
        }}
        data-repository-id={group.primaryRow.repositoryId}
        role="row"
      >
        <TableCell
          className="order-5 col-span-2 p-0 first:pl-0 lg:table-cell lg:w-8 lg:px-2 lg:first:pl-6"
          role="cell"
        >
          <button
            type="button"
            onClick={handleButtonClick}
            aria-expanded={isExpanded}
            // Only while open, same as the card: no panel in the DOM, no
            // aria-controls pointing at it.
            aria-controls={isExpanded ? detailsId : undefined}
            data-testid="contribution-card-toggle"
            className="ring-offset-background focus:ring-ring text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded-sm text-base transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none lg:justify-center lg:opacity-70 lg:hover:opacity-100"
          >
            <span
              className="size-4 shrink-0 lg:hidden"
              aria-hidden="true"
              data-contribution-count-icon
              data-testid="contribution-count-icon"
            />
            <span className="tabular-nums lg:hidden">
              {contributionCount}{" "}
              {contributionCount === 1 ? "contribution" : "contributions"}
            </span>
            {chevronIcon("size-4 shrink-0")}
            <span className="sr-only">
              {isExpanded ? "Collapse" : "Expand"} details for{" "}
              {group.primaryRow.repositoryName}
            </span>
          </button>
        </TableCell>
        <TableCell
          className="order-1 min-w-0 p-0 font-medium lg:table-cell lg:p-4 lg:whitespace-nowrap lg:first:pl-6"
          role="cell"
        >
          <ExternalLink
            href={group.primaryRow.repositoryUrl}
            className="min-w-0 wrap-anywhere hover:underline"
            onClick={(event) => event.stopPropagation()}
            data-testid="contribution-repository-link"
          >
            <RepositoryLinkLabel name={group.primaryRow.repositoryName} />
          </ExternalLink>
        </TableCell>
        <TableCell
          className="order-2 p-0 text-right lg:table-cell lg:p-4"
          data-testid="contribution-stars"
          role="cell"
        >
          <div className="flex items-center justify-end gap-1">
            {starIcon("size-4 fill-yellow-400 text-yellow-400")}
            <span className="text-base font-medium tabular-nums lg:font-normal">
              {formatStars(group.maxStars)}
            </span>
          </div>
        </TableCell>
        <TableCell
          className="order-3 col-span-2 p-0 lg:table-cell lg:p-4"
          role="cell"
        >
          <ContributionTechnologyBadges
            technologies={group.primaryRow.technologies}
          />
        </TableCell>
        <TableCell
          className="text-muted-foreground order-4 col-span-2 p-0 text-base lg:table-cell lg:p-4 lg:whitespace-nowrap"
          role="cell"
        >
          <span className="lg:hidden">Contributed in </span>
          {formatYears(years)}
        </TableCell>
        <TableCell className="hidden text-right lg:table-cell" role="cell">
          <div className="flex items-center justify-end gap-1">
            {gitCommitHorizontalIcon("text-muted-foreground size-4")}
            <span className="tabular-nums">{commitCount}</span>
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow
          className="block border-0 hover:bg-transparent lg:table-row"
          role="row"
        >
          <TableCell
            colSpan={6}
            className="block p-4 pt-0 sm:p-5 sm:pt-0 lg:table-cell lg:p-6"
            role="cell"
          >
            {/* Absent from the prerendered HTML on purpose. See
                adr/0007-keep-contribution-details-behind-a-disclosure.md. */}
            <ContributionGroupDetails
              id={detailsId}
              group={group}
              searchQuery={query}
              className="space-y-4 lg:space-y-6"
            />
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
});
